import { useState } from 'react'
import { MinimalSelect } from '../dropdown-input'

type PropTypes = {
  prefix?: string
  currentPage: number
  lastPage: number
  from: number
  to: number
  total: number
  onPageChange: (pageNum: number) => void
  onRowsPerPageChange: (quantity: number) => void
}

/**
 * When using this component, use the usePagination hook on the same page for further pagination control.
 */
const PaginationBar = ({ prefix = '', currentPage, lastPage, from, to, total, onPageChange, onRowsPerPageChange }: PropTypes) => {
  const textSpec = {
    rowsPerPageLabel: 'Rows per page ',
    previousButtonLabel: 'Prev',
    nextButtonLabel: 'Next',
    pageCountPrefix: 'Showing ',
    fromToSeparator: ' - ',
    currentTotalSeparator: ' of ',
    pageCountPostfix: ' results',
  }

  const paginationOptions = [
    { value: 15, label: '15' },
    { value: 30, label: '30' },
    { value: 45, label: '45' },
  ]

  const [defaultItemsPerRow, setDefaultItemsPerRow] = useState<number>(15)

  const isNextButtonDisabled = currentPage === lastPage
  const isPrevButtonDisabled = currentPage === 1

  const searchParams = new URLSearchParams(window.location.search)
  const paramValue = searchParams.get(`${prefix}PerPage`)

  if (paramValue !== null) {
    const parsedValue = parseInt(paramValue, 10)
    if (parsedValue !== defaultItemsPerRow) {
      if (paginationOptions.find((option) => option.value === parsedValue) === undefined) {
        return
      }

      setDefaultItemsPerRow(isNaN(parsedValue) ? defaultItemsPerRow : parsedValue)
    }
  }

  const handleNextPage = () => {
    return onPageChange(Math.min(currentPage + 1, lastPage))
  }

  const handlePrevPage = () => {
    return onPageChange(Math.max(currentPage - 1, 1))
  }

  return (
    <nav className="flex items-center justify-between border rounded-lg border-gray-400 bg-gray-50 py-3 px-4">

      {/* Rows per page */}
      <div className="flex flex-row items-center justify-center gap-2 md:w-[146px]">
        <span className="hidden md:block text-gray-700 text-sm font-medium leading-[19.6px]">
          {textSpec.rowsPerPageLabel}
        </span>
        <MinimalSelect
          defaultValue={defaultItemsPerRow}
          options={paginationOptions}
          onChange={(option) => onRowsPerPageChange?.(option.value)}
        />
      </div>

      {/* Page info */}
      <div className="hidden md:block text-gray-700 text-sm font-medium leading-[19.6px]">
        <span>{textSpec.pageCountPrefix}</span>
        <span>{from ?? 0}{textSpec.fromToSeparator}{(to ?? 0)}</span>
        <span>{textSpec.currentTotalSeparator}{total}{textSpec.pageCountPostfix}</span>
      </div>

      {/* Next - Prev buttons */}
      <div className="flex gap-x-[1rem] md:gap-x-[1.875rem] md:justify-end md:w-[146px]">
        <button
          onClick={() => handlePrevPage()}
          disabled={isPrevButtonDisabled}
          className="disabled:text-gray-400  text-gray-700 text-sm font-semibold leading-[19.6px] outline-offset-4"
        >
          Prev
        </button>
        <button
          onClick={() => handleNextPage()}
          disabled={isNextButtonDisabled}
          className="disabled:text-gray-400 text-gray-700 text-sm font-semibold leading-[19.6px] outline-offset-4"
        >
          Next
        </button>
      </div>
    </nav>
  )
}

export default PaginationBar
